import React, { createContext, useContext, useMemo } from 'react';
import { supabase } from '../util/supabaseClient';

// API 기본 URL
const API_URL = process.env.REACT_APP_API_URL;

// Helper to get auth headers from current Supabase session
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (session?.access_token) {
    return { Authorization: `Bearer ${session.access_token}` };
  }
  return {};
};

// Context 생성
const ApiContext = createContext({});

// Custom hook을 만들어 Context 사용을 더 쉽게 함
export const useApiContext = () => {
  return useContext(ApiContext);
};

// Provider 컴포넌트
export const ApiProvider = ({ children }) => {
  // 일반적인 fetch 래퍼 함수
  const fetchData = async (endpoint, options = {}) => {
    try {
      const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
      const authHeaders = await getAuthHeaders();
      const mergedOptions = {
        ...options,
        headers: {
          ...authHeaders,
          ...options.headers,
        },
      };
      const response = await fetch(url, mergedOptions);
      
      // 응답 본문 파싱
      let responseData;
      try {
        responseData = await response.json();
      } catch (jsonError) {
        responseData = null;
      }
      
      // 응답이 정상이 아닌 경우
      if (!response.ok) {
        let errorMessage = '요청 처리 중 오류가 발생했습니다.';
        let retry = false;
        
        // 상태 코드별 에러 메시지 처리
        switch (response.status) {
          case 504:
            errorMessage = '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
            retry = true;
            break;
          case 503:
            errorMessage = '서버가 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해주세요.';
            retry = true;
            break;
          case 400:
            errorMessage = responseData?.message || '잘못된 요청입니다.';
            break;
          default:
            errorMessage = responseData?.message || `API 요청 실패: ${response.status}`;
        }
        
        // 백엔드에서 retry 플래그를 명시적으로 보낸 경우 우선 적용
        if (responseData && typeof responseData.retry !== 'undefined') {
          retry = responseData.retry;
        }
        
        const error = new Error(errorMessage);
        error.retry = retry;
        error.status = response.status;
        error.data = responseData;
        throw error;
      }
      
      return { data: responseData };
    } catch (error) {
      const errorMessage = error.message || '알 수 없는 오류가 발생했습니다.';
      const errorObj = {
        message: errorMessage,
        retry: error.retry || false,
        status: error.status,
        data: error.data
      };
      return { error: errorObj };
    }
  };

  // GET 요청
  const get = async (endpoint, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    return fetchData(url);
  };

  // POST 요청
  const post = async (endpoint, body) => {
    return fetchData(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  };

  // 아파트 정보 조회
  const getAptInfo = async (aptData) => {
    const bodyData = typeof aptData === 'string' 
      ? { aptNumber1: aptData, aptNumber2: "", aptNumber3: "" } 
      : aptData;
    
    return post('/apt', { data: bodyData });
  };

  // 아파트 매물 정보 조회
  const getAptSaleInfo = async (apt_no, pyeongNo, isRent) => {
    return post('/apt_sale_info', { apt_no, pyeongNo, isRent });
  };

  // 아파트 메트릭스 정보 조회
  const getAptMetrixInfo = async (regionInfo) => {
    // 다중 선택 여부에 따라 API 요청 본문 구성
    let requestBody;
    
    // 다중 선택 케이스
    if (regionInfo.multipleSelect && Array.isArray(regionInfo.cityCode)) {
      requestBody = {
        data: { 
          regionCode: regionInfo.regionCode || "",
          regionName: regionInfo.regionName || "",
          cityCodes: regionInfo.cityCode,  // 배열
          cityNames: regionInfo.cityName,  // 배열
          multipleSelect: true
        }
      };
    } 
    // 단일 선택 또는 전체 선택 케이스
    else {
      requestBody = {
        data: { 
          regionCode: regionInfo.regionCode || "",
          regionName: regionInfo.regionName || "",
          cityCode: regionInfo.cityCode || "",
          cityName: regionInfo.cityName || ""
        }
      };
    }
    
    return post('/api/aptmetrix', requestBody);
  };

  // 구독 취소 요청
  const requestCancelSubscription = async () => {
    return post('/request-cancel', {});
  };

  // 무료 체험 확인
  const checkFreeTrial = async () => {
    return get('/check-free-trial');
  };

  // 무료 체험 활성화
  const activateFreeTrial = async () => {
    return post('/activate-free-trial', {});
  };

  // 결제 세션 생성
  const createCheckoutSession = async (priceId, planName, price) => {
    return post('/create-checkout-session', {
      priceId,
      planName,
      price
    });
  };

  // 구독 확인
  const verifySubscription = async (sessionId) => {
    return post('/verify-subscription', {
      sessionId
    });
  };

  // 문의 메시지 전송
  const sendContactMessage = async (webhookUrl, formData) => {
    return fetchData(webhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        message: formData.message
      })
    });
  };

  // 아파트 필터링 검색
  const filterApartments = async (filters) => {
    const requestBody = { filters };
    return post('/api/filter-apartments', requestBody);
  };

  // 관심 목록 조회
  const getWatchlist = async () => {
    return get('/api/watchlist');
  };

  // 관심 목록에 아파트 추가
  const addWatchlistApartment = async (apartmentData) => {
    return post('/api/watchlist/apartment', apartmentData);
  };

  // 관심 목록에 조건 추가
  const addWatchlistCondition = async (conditionData) => {
    return post('/api/watchlist/condition', conditionData);
  };

  // 관심 목록 항목 수정
  const updateWatchlistItem = async (id, updates) => {
    return fetchData(`/api/watchlist/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updates),
    });
  };

  // 관심 목록 항목 삭제
  const deleteWatchlistItem = async (id) => {
    return fetchData(`/api/watchlist/${id}`, {
      method: 'DELETE',
    });
  };

  // API 함수를 객체로 묶어서 제공 (stable reference)
  const api = useMemo(() => ({
    getAptInfo,
    getAptSaleInfo,
    getAptMetrixInfo,
    requestCancelSubscription,
    checkFreeTrial,
    activateFreeTrial,
    createCheckoutSession,
    verifySubscription,
    sendContactMessage,
    filterApartments,
    getWatchlist,
    addWatchlistApartment,
    addWatchlistCondition,
    updateWatchlistItem,
    deleteWatchlistItem,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // Context에 제공할 값
  const contextValue = useMemo(() => ({ api }), [api]);

  return (
    <ApiContext.Provider value={contextValue}>
      {children}
    </ApiContext.Provider>
  );
}; 